import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Typography,
    Box,
    useTheme,
    useMediaQuery,
    Divider,
    Autocomplete,
    IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import config from "../config/config";
import { formatNumber } from "../utils/helpers";
import AddCustomerDialog from "./AddCustomerDialog";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const parseNumber = (str) => {
    if (str === null || str === undefined || str === "") return 0;
    const parsed = parseFloat(String(str).replace(/\./g, ""));
    return isNaN(parsed) ? 0 : parsed;
};

const AddOrderDialogMain = ({ open, onClose, onOrderAdded }) => {
    const { token } = useAuth();
    const { showToast } = useToast();
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [orderItems, setOrderItems] = useState([]);
    const [form, setForm] = useState({
        customerId: "",
        paymentMethod: "Cash",
        amountPaid: 0,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [customerDebt, setCustomerDebt] = useState(0);
    const [projectedDebt, setProjectedDebt] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [productPage, setProductPage] = useState(1);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [isFetchingProducts, setIsFetchingProducts] = useState(false);
    const [productSearch, setProductSearch] = useState(""); // To hold the search input

    const [customerPage, setCustomerPage] = useState(1);
    const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
    const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
    const [customerSearch, setCustomerSearch] = useState("");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const totalAmount = useMemo(() => {
        return orderItems.reduce(
            (acc, item) =>
                acc + (item.priceAdjust - item.discountAmount) * item.quantity,
            0
        );
    }, [orderItems]);

    useEffect(() => {
        if (open) {
            setCustomers([]);
            fetchCustomers(1, "");
            setProducts([]); // Reset products on open
            fetchProducts(1, productSearch); // Fetch initial products
            // Reset form when dialog opens
            setForm({
                customerId: "",
                paymentMethod: "Cash",
                amountPaid: 0,
            });
            setOrderItems([]);
            setCustomerDebt(0);
            setProjectedDebt(0);
            setValidationErrors({});
            setSelectedProduct(null);
            setQuantity(1);
        }
        // eslint-disable-next-line
    }, [open]);

    // Debounced search
    useEffect(() => {
        const handler = setTimeout(() => {
            setProducts([]); // Reset products
            fetchProducts(1, productSearch);
        }, 500); // 500ms debounce

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line
    }, [productSearch]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setCustomers([]);
            fetchCustomers(1, customerSearch);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line
    }, [customerSearch]);

    useEffect(() => {
        if (form.customerId) {
            const newDebt = customerDebt + (totalAmount - form.amountPaid);
            setProjectedDebt(newDebt);
        }
    }, [totalAmount, form.amountPaid, customerDebt, form.customerId]);

    const fetchCustomers = async (page, search = "") => {
        if (isFetchingCustomers) return;
        setIsFetchingCustomers(true);
        try {
            const res = await axios.get(`${config.backendUrl}/api/customers`, {
                headers: { "x-auth-token": token },
                params: { page, limit: 10, search },
            });
            const newCustomers = res.data.docs || [];
            setCustomers((prev) =>
                page === 1 ? newCustomers : [...prev, ...newCustomers]
            );
            setHasMoreCustomers(res.data.hasNextPage);
            setCustomerPage(page + 1);
        } catch (err) {
            console.error("Error fetching customers:", err);
            showToast("Không thể tải danh sách khách hàng.", "error");
        } finally {
            setIsFetchingCustomers(false);
        }
    };

    const fetchProducts = async (page, search = "") => {
        if (isFetchingProducts) return;
        setIsFetchingProducts(true);
        try {
            const res = await axios.get(`${config.backendUrl}/api/products`, {
                headers: { "x-auth-token": token },
                params: { page, limit: 10, search }, // Assuming backend supports search and pagination
            });
            const newProducts = res.data.docs || [];
            setProducts((prev) =>
                page === 1 ? newProducts : [...prev, ...newProducts]
            );
            setHasMoreProducts(res.data.hasNextPage);
            setProductPage(page + 1);
        } catch (err) {
            console.error("Error fetching products:", err);
            showToast("Không thể tải danh sách sản phẩm.", "error");
        } finally {
            setIsFetchingProducts(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleCustomerAdded = (newCustomer) => {
        setCustomers((prev) => [...prev, newCustomer]);
        setForm({ ...form, customerId: newCustomer._id });
        setCustomerDebt(newCustomer.debt || 0);
        setAddCustomerDialogOpen(false);
    };

    const handleAddProductToOrder = () => {
        if (!selectedProduct) {
            showToast("Vui lòng chọn một sản phẩm.", "warning");
            return;
        }
        if (quantity <= 0) {
            showToast("Số lượng phải lớn hơn 0.", "warning");
            return;
        }
        if (quantity > selectedProduct.quantity) {
            showToast(
                `Số lượng không được vượt quá số lượng tồn kho (${selectedProduct.quantity}).`,
                "warning"
            );
            return;
        }

        const existingItemIndex = orderItems.findIndex(
            (item) => item.product._id === selectedProduct._id
        );

        if (existingItemIndex > -1) {
            const updatedItems = [...orderItems];
            const newQuantity =
                updatedItems[existingItemIndex].quantity + quantity;
            if (newQuantity > selectedProduct.quantity) {
                showToast(
                    `Tổng số lượng vượt quá số lượng tồn kho (${selectedProduct.quantity}).`,
                    "warning"
                );
                return;
            }
            updatedItems[existingItemIndex].quantity = newQuantity;
            setOrderItems(updatedItems);
        } else {
            const newItem = {
                product: selectedProduct,
                quantity: quantity,
                price: selectedProduct.price,
                priceAdjust: selectedProduct.price, // Default to product.price
                discountAmount: selectedProduct.discountAmount || 0,
                priceAfterDiscount: selectedProduct.price,
                imageUrl: selectedProduct.imageUrl,
            };
            setOrderItems([...orderItems, newItem]);
        }

        setSelectedProduct(null);
        setQuantity(1);
    };

    const handleRemoveItem = (productId) => {
        setOrderItems(
            orderItems.filter((item) => item.product._id !== productId)
        );
    };

    const handleItemPriceAdjustChange = (productId, value) => {
        setOrderItems((prevItems) =>
            prevItems.map((item) =>
                item.product._id === productId
                    ? { ...item, priceAdjust: parseNumber(value) }
                    : item
            )
        );
    };

    const handleItemDiscountAmountChange = (productId, value) => {
        setOrderItems((prevItems) =>
            prevItems.map((item) =>
                item.product._id === productId
                    ? { ...item, discountAmount: parseNumber(value) }
                    : item
            )
        );
    };

    const handleItemDiscountAmountBlur = (productId, value) => {
        setOrderItems((prevItems) =>
            prevItems.map((item) => {
                const parsedValue = parseNumber(value);
                const newDiscountAmount = Math.min(
                    item.priceAdjust * item.quantity,
                    Math.max(0, parsedValue)
                );
                return item.product._id === productId
                    ? {
                          ...item,
                          discountAmount: newDiscountAmount,
                          priceAfterDiscount:
                              item.priceAdjust - newDiscountAmount,
                      }
                    : item;
            })
        );
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!form.customerId) {
            errors.customer = "Vui lòng chọn một khách hàng.";
        }
        if (orderItems.length === 0) {
            errors.items = "Vui lòng thêm ít nhất một sản phẩm vào đơn hàng.";
        }
        if (!form.paymentMethod) {
            errors.paymentMethod = "Phương thức thanh toán là bắt buộc";
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setIsSubmitting(true);
        try {
            const totalDiscountPrice = orderItems.reduce(
                (acc, item) => acc + item.discountAmount,
                0
            );

            const orderData = {
                items: orderItems.map((item) => ({
                    product: item.product._id,
                    quantity: item.quantity,
                    lastQuantity: item.product.quantity,
                    price: item.price, // Original price for reference
                    priceAdjust: item.priceAdjust, // New field
                    priceAfterDiscount:
                        item.priceAdjust - (item.discountAmount || 0),
                    discountAmount: item.discountAmount || 0, // Ensure discountAmount is a number
                })),
                customerId: form.customerId,
                totalAmount: totalAmount,
                status: "Created",
                paymentMethod: form.paymentMethod,
                discountPrice: totalDiscountPrice,
                amountPaid: form.amountPaid === "" ? 0 : form.amountPaid,
            };
            const res = await axios.post(
                `${config.backendUrl}/api/orders`,
                orderData,
                {
                    headers: { "x-auth-token": token },
                }
            );

            showToast("Đơn hàng đã được tạo thành công!", "success");
            if (onOrderAdded) {
                const order = res.data;
                const customer = customers.find(
                    (cust) => cust._id.toString() === order.customer
                );
                const convertOrder = {
                    ...order,
                    customer,
                };
                onOrderAdded(convertOrder);
            }
            handleClose();
        } catch (err) {
            console.error(err);
            const errorMessage =
                err.response?.data?.message ||
                "Đã xảy ra lỗi khi tạo đơn hàng.";
            showToast(errorMessage, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                fullScreen={isMobile}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Thêm đơn hàng</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        options={products}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                            option._id === value._id
                        }
                        value={selectedProduct}
                        onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                                setProductSearch(newInputValue);
                            }
                        }}
                        onChange={(event, newValue) => {
                            setSelectedProduct(newValue);
                        }}
                        ListboxProps={{
                            onScroll: (event) => {
                                const listboxNode = event.currentTarget;
                                if (
                                    listboxNode.scrollTop +
                                        listboxNode.clientHeight ===
                                        listboxNode.scrollHeight &&
                                    hasMoreProducts &&
                                    !isFetchingProducts
                                ) {
                                    fetchProducts(productPage, productSearch);
                                }
                            },
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <Box display="flex" alignItems="center">
                                    <Box
                                        component="img"
                                        src={`${config.backendUrl}${option.imageUrl}`}
                                        alt={option.name}
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            borderRadius: 1,
                                            mr: 1,
                                        }}
                                    />
                                    <Typography>{option.name}</Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tìm kiếm sản phẩm"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                            />
                        )}
                    />
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <TextField
                            sx={{ width: "100px" }}
                            label="Số lượng"
                            // type="number"
                            value={quantity}
                            onChange={(e) =>
                                setQuantity(parseInt(e.target.value, 10) || 0)
                            }
                            inputProps={{ min: 1 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAddProductToOrder}
                            startIcon={<AddIcon />}
                        >
                            Thêm sản phẩm
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6">
                        Sản phẩm trong đơn hàng
                    </Typography>
                    {orderItems.length === 0 ? (
                        <Typography>Chưa có sản phẩm nào.</Typography>
                    ) : (
                        orderItems.map((item, index) => (
                            <Box
                                key={index}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                border="1px solid #ddd"
                                borderRadius={1}
                                p={1}
                                mb={1}
                            >
                                <Box display="flex" alignItems="center">
                                    <Box
                                        component="img"
                                        src={`${config.backendUrl}${item.imageUrl}`}
                                        alt={item.product.name}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 1,
                                            mr: 1,
                                        }}
                                    />
                                    <Box>
                                        <Typography>
                                            {`${item.product.name} - Tồn kho: ${item.quantity}`}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Giá gốc: {formatNumber(item.price)}{" "}
                                            ₫
                                        </Typography>
                                        <TextField
                                            label="Đơn giá"
                                            variant="outlined"
                                            size="small"
                                            value={formatNumber(
                                                item.priceAdjust
                                            )}
                                            onChange={(e) =>
                                                handleItemPriceAdjustChange(
                                                    item.product._id,
                                                    e.target.value
                                                )
                                            }
                                            inputProps={{ min: 0 }}
                                            sx={{ mt: 1, width: "120px" }}
                                        />
                                        <TextField
                                            label={`Giảm giá (${
                                                item.priceAdjust > 0
                                                    ? (
                                                          (item.discountAmount /
                                                              item.priceAdjust) *
                                                          100
                                                      ).toFixed(0)
                                                    : 0
                                            }%)`}
                                            variant="outlined"
                                            size="small"
                                            value={formatNumber(
                                                item.discountAmount
                                            )}
                                            onChange={(e) =>
                                                handleItemDiscountAmountChange(
                                                    item.product._id,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e) =>
                                                handleItemDiscountAmountBlur(
                                                    item.product._id,
                                                    e.target.value
                                                )
                                            }
                                            inputProps={{ min: 0 }}
                                            sx={{ mt: 1, width: "120px" }}
                                        />
                                    </Box>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <Typography
                                        fontWeight="bold"
                                        color="primary"
                                        sx={{ mr: 2 }}
                                    >
                                        {formatNumber(
                                            item.priceAdjust * item.quantity
                                        )}{" "}
                                        ₫
                                    </Typography>
                                    <IconButton
                                        onClick={() =>
                                            handleRemoveItem(item.product._id)
                                        }
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Autocomplete
                        options={[
                            {
                                _id: "add_new",
                                name: "Thêm khách hàng",
                                isAddNew: true,
                            },
                            ...customers,
                        ]}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                            option._id === value._id
                        }
                        value={
                            customers.find((c) => c._id === form.customerId) ||
                            null
                        }
                        onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                                setCustomerSearch(newInputValue);
                            }
                        }}
                        ListboxProps={{
                            onScroll: (event) => {
                                const listboxNode = event.currentTarget;
                                if (
                                    listboxNode.scrollTop +
                                        listboxNode.clientHeight ===
                                        listboxNode.scrollHeight &&
                                    hasMoreCustomers &&
                                    !isFetchingCustomers
                                ) {
                                    fetchCustomers(
                                        customerPage,
                                        customerSearch
                                    );
                                }
                            },
                        }}
                        onChange={(event, newValue) => {
                            if (newValue?.isAddNew) {
                                setAddCustomerDialogOpen(true);
                            } else {
                                setForm({
                                    ...form,
                                    customerId: newValue ? newValue._id : "",
                                });
                                setCustomerDebt(newValue ? newValue.debt : 0);
                            }
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                {option.isAddNew ? (
                                    <Button
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        sx={{
                                            color: "primary.main",
                                            fontWeight: "bold",
                                        }}
                                        fullWidth
                                    >
                                        Thêm khách hàng
                                    </Button>
                                ) : (
                                    <>
                                        {option.name}{" "}
                                        {option.phone && `(${option.phone})`}
                                    </>
                                )}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Khách hàng"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!validationErrors.customer}
                                helperText={validationErrors.customer}
                            />
                        )}
                    />

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 2 }}
                    >
                        <Typography fontWeight="bold">
                            Tổng tiền: {formatNumber(totalAmount)} ₫
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                                setForm({ ...form, amountPaid: totalAmount })
                            }
                        >
                            Áp dụng
                        </Button>
                    </Box>

                    <TextField
                        label="Số tiền thanh toán"
                        fullWidth
                        margin="normal"
                        value={
                            form.amountPaid === 0
                                ? ""
                                : formatNumber(form.amountPaid)
                        }
                        onChange={(e) => {
                            const val = e.target.value;
                            setForm({
                                ...form,
                                amountPaid: val === "" ? 0 : parseNumber(val),
                            });
                        }}
                        inputProps={{ min: 0 }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                        Công nợ dự kiến sau đơn hàng:{" "}
                        {formatNumber(projectedDebt)}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Hủy</Button>
                    <LoadingButton
                        onClick={handleSubmit}
                        variant="contained"
                        loading={isSubmitting}
                        loadingIndicator="Đang xử lý..."
                    >
                        Thêm đơn hàng
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <AddCustomerDialog
                open={addCustomerDialogOpen}
                onClose={() => setAddCustomerDialogOpen(false)}
                onCustomerAdded={handleCustomerAdded}
            />
        </>
    );
};

export default AddOrderDialogMain;
